const TRANSPORTS = new Set(['udp', 'tcp']);
const CANDIDATE_TYPES = new Set(['host', 'srflx', 'prflx', 'relay']);
const TCP_TYPES = new Set(['active', 'passive', 'so']);
const COMPONENT = /^\d{1,3}$/;
const TOKEN = /^[\w+/.-]{1,256}$/;
const oneOf = (values, value) => {
    const normalized = value?.toLowerCase();
    return normalized !== undefined && values.has(normalized)
        ? normalized
        : 'unknown';
};
const summarizeAttribute = (attribute) => {
    const value = attribute.replace(/^(a=)?candidate:/i, '').trim();
    if (!value) {
        return 'end-of-candidates';
    }
    // RFC 8839 §5.1: foundation component transport priority address port
    // "typ" type *(extension-name extension-value)
    const [, component, transport, , , , typ, type, ...extensions] = value.split(/\s+/);
    if (!component || !COMPONENT.test(component) || typ !== 'typ') {
        return 'unparsed';
    }
    const tcpTypeIndex = extensions.indexOf('tcptype');
    return {
        component,
        transport: oneOf(TRANSPORTS, transport),
        type: oneOf(CANDIDATE_TYPES, type),
        ...(tcpTypeIndex >= 0 && {
            tcpType: oneOf(TCP_TYPES, extensions[tcpTypeIndex + 1]),
        }),
    };
};
const token = (value) => typeof value === 'string' && TOKEN.test(value) ? value : undefined;
/**
 * Reads a field, including the accessors `RTCIceCandidate` defines. A field
 * that cannot be read is treated as absent, so logging never interrupts
 * negotiation.
 */
const field = (value, key) => {
    try {
        return key in value ? Reflect.get(value, key) : undefined;
    }
    catch {
        return undefined;
    }
};
/**
 * Summarizes an ICE candidate for logs. Candidate addresses identify
 * participants' networks, so only the component, transport, and type are
 * kept.
 *
 * @param value - an `RTCIceCandidate`, an `RTCIceCandidateInit`, an Infinity
 * `new_candidate` event, or `null`, which ends local candidate gathering
 *
 * @example
 * summarizeIceCandidate({
 *     candidate: 'candidate:1 1 UDP 1686052863 192.0.2.1 5000 typ srflx raddr 10.0.0.1 rport 5000',
 *     mid: '0',
 * });
 * // {mid: '0', candidate: {component: '1', transport: 'udp', type: 'srflx'}}
 */
export const summarizeIceCandidate = (value) => {
    if (value === null) {
        return { candidate: 'end-of-candidates' };
    }
    if (typeof value !== 'object') {
        return { candidate: 'unparsed' };
    }
    const candidate = field(value, 'candidate');
    const mid = token(field(value, 'mid') ?? field(value, 'sdpMid'));
    const ufrag = token(field(value, 'ufrag') ?? field(value, 'usernameFragment'));
    return {
        ...(mid && { mid }),
        ...(ufrag && { ufrag }),
        candidate: typeof candidate === 'string'
            ? summarizeAttribute(candidate)
            : 'unparsed',
    };
};
//# sourceMappingURL=iceCandidateSummary.js.map